import React, { forwardRef, useCallback, useState } from 'react';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** Optional click handler. If it returns a Promise, the button shows loading until it settles. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  /** Override loading state (e.g. when parent controls it, e.g. form submit). */
  loading?: boolean;
  /** Optional extra class names. Merged with base and loading state. */
  className?: string;
  children?: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { onClick, loading: loadingProp, className = '', disabled, children, type = 'button', ...rest },
  ref
) {
  const [internalLoading, setInternalLoading] = useState(false);
  const loading = loadingProp === true || internalLoading;
  const isDisabled = disabled || loading;

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!onClick || isDisabled) return;
      const result = onClick(event);
      if (result instanceof Promise) {
        setInternalLoading(true);
        try {
          await result;
        } finally {
          setInternalLoading(false);
        }
      }
    },
    [onClick, isDisabled]
  );

  return (
    <button
      ref={ref}
      type={type}
      className={className}
      disabled={isDisabled}
      onClick={handleClick}
      aria-busy={loading}
      aria-disabled={isDisabled}
      {...rest}
    >
      {loading ? (
        <>
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
          {children}
        </>
      ) : (
        children
      )}
    </button>
  );
});

export default Button;
